/**
 * formatSystemActivity — Iter · Feb 2026
 *
 * Renders task/system activity messages in a human-friendly way:
 *   • Raw ISO datetimes ("2026-08-17T11:57:54.891000+00:00") → "17 Aug '26 · 5:27 PM"
 *   • Status transitions ("status: open → in_progress") → chip-style
 *     `Status` label + coloured pills for old / new values
 *   • Percent complete, priority, health etc. get the same
 *     label-with-chips treatment where practical
 *
 * Returns React nodes so the caller can drop the result inline into a
 * <div>. Keeps the whitespace-pre-wrap semantics of the original text
 * where transformation isn't possible (custom messages).
 */
import React from "react";
import { fmtDateTime } from "@/lib/format";

// Field slug → user-facing label
const FIELD_LABELS = {
  status: "Status",
  priority: "Priority",
  health: "Health",
  progress_percent: "% complete",
  "% complete": "% complete",
  actual_start: "Actual start",
  actual_start_date: "Actual start",
  actual_end: "Actual completion",
  actual_end_date: "Actual completion",
  start_date: "Planned start",
  due_date: "Due date",
  completed_at: "Completed at",
  assignee: "Assignee",
  assignee_id: "Assignee",
};

const STATUS_CHIP = {
  open: "bg-neutral-100 text-neutral-700 border-neutral-300",
  todo: "bg-neutral-100 text-neutral-700 border-neutral-300",
  in_progress: "bg-sky-100 text-sky-700 border-sky-300",
  review: "bg-violet-100 text-violet-700 border-violet-300",
  blocked: "bg-rose-100 text-rose-700 border-rose-300",
  done: "bg-emerald-100 text-emerald-700 border-emerald-300",
};

const PRIORITY_CHIP = {
  low: "bg-neutral-100 text-neutral-700 border-neutral-300",
  medium: "bg-amber-100 text-amber-700 border-amber-300",
  high: "bg-orange-100 text-orange-700 border-orange-300",
  urgent: "bg-rose-100 text-rose-700 border-rose-300",
};

const HEALTH_CHIP = {
  on_track: "bg-emerald-100 text-emerald-700 border-emerald-300",
  at_risk: "bg-amber-100 text-amber-700 border-amber-300",
  off_track: "bg-rose-100 text-rose-700 border-rose-300",
};

// Detect ISO 8601 datetime strings (with or without fractional seconds &
// timezone offset). Anchored so it doesn't grab partial dates.
const ISO_DT_RE = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:[+-]\d{2}:?\d{2}|Z)?/;

// Match "<field>: <oldVal> → <newVal>" (arrow can be `→` or `->`).
// Field can contain letters, digits, underscore, spaces, `%`.
const FIELD_TRANSITION_RE = /^([A-Za-z%][\w %]*):\s*(.+?)\s*(?:→|->)\s*(.+)$/;

const _prettyDate = (s) => {
  if (!s || s === "—") return "—";
  if (ISO_DT_RE.test(s)) {
    try { return fmtDateTime(s); } catch { /* fall through */ }
  }
  return s;
};

const _fieldLabel = (raw) => FIELD_LABELS[raw] || FIELD_LABELS[raw.toLowerCase()] || raw;

const _chipCls = (field, value) => {
  const v = String(value || "").trim().toLowerCase();
  if (field === "status") return STATUS_CHIP[v] || "bg-neutral-100 text-neutral-700 border-neutral-300";
  if (field === "priority") return PRIORITY_CHIP[v] || "bg-neutral-100 text-neutral-700 border-neutral-300";
  if (field === "health") return HEALTH_CHIP[v] || "bg-neutral-100 text-neutral-700 border-neutral-300";
  return "bg-neutral-100 text-neutral-700 border-neutral-300";
};

const _renderValue = (fieldRaw, value) => {
  const v = _prettyDate(String(value ?? "").trim());
  const empty = !v || v === "—" || v.toLowerCase() === "none" || v.toLowerCase() === "null";
  if (empty) {
    return <span className="text-muted-foreground italic">empty</span>;
  }
  const chipFields = new Set(["status", "priority", "health"]);
  if (chipFields.has(fieldRaw)) {
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 border text-[10px] uppercase tracking-widest ${_chipCls(fieldRaw, value)}`}>
        {String(v).replace(/_/g, " ")}
      </span>
    );
  }
  return <span className="font-mono text-foreground/85">{v}</span>;
};

/**
 * Given a raw system-message body, return a React node with any
 * "<field>: old → new" line transformed into a chip-style row, and any
 * embedded ISO datetime replaced with the friendly `fmtDateTime` output.
 * Falls back to the original text when nothing matches.
 */
export function formatSystemActivity(rawText) {
  const text = String(rawText || "").trim();
  if (!text) return null;

  const lines = text.split("\n");
  const nodes = lines.map((line, i) => {
    const m = line.match(FIELD_TRANSITION_RE);
    if (m) {
      const [, fieldRaw, oldV, newV] = m;
      const fieldSlug = fieldRaw.trim().toLowerCase().replace(/\s+/g, "_");
      const label = _fieldLabel(fieldRaw.trim());
      return (
        <div key={i} className="flex items-center gap-1.5 flex-wrap text-[13px] leading-relaxed">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">{label}</span>
          {_renderValue(fieldSlug, oldV)}
          <span className="text-muted-foreground/60">→</span>
          {_renderValue(fieldSlug, newV)}
        </div>
      );
    }
    // No field-transition — just replace any embedded ISO datetime.
    const replaced = line.replace(new RegExp(ISO_DT_RE.source, "g"), (m2) => {
      try { return fmtDateTime(m2); } catch { return m2; }
    });
    return (
      <div key={i} className="text-sm leading-relaxed whitespace-pre-wrap break-words text-foreground/80">
        {replaced}
      </div>
    );
  });
  return <div className="space-y-1">{nodes}</div>;
}
